import * as React from "react";
import { Box, Typography, Checkbox as JoyCheckbox, Divider, Grid, Button } from "@mui/joy";
import { useFormContext } from "react-hook-form";
import { usePermissions } from "../../../../../hooks/query";
import { TextField, Textarea } from "../../../../../shared";
import { CheckCircle2, Circle, ListPlus, ListX } from "lucide-react";

/**
 * Groups permissions by their prefix (e.g., "Supplier", "Role", "User")
 */
function groupPermissions(permissions: any[]) {
    const groups: Record<string, any[]> = {};

    permissions.forEach((perm) => {
        let groupName = "General";
        const name = perm.Name || perm.Id;

        if (name.includes(":")) {
            groupName = name.split(":")[0].trim();
        } else if (name.includes(".")) {
            groupName = name.split(".")[0].trim();
            groupName = groupName.charAt(0).toUpperCase() + groupName.slice(1);
        } else {
            const keywords = ["Supplier", "Role", "User", "Project", "Lead", "Inventory", "Permission"];
            const found = keywords.find((k) => name.toLowerCase().includes(k.toLowerCase()));
            if (found) groupName = found;
        }

        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(perm);
    });

    return groups;
}

export default function RoleConfiguration() {
    const { useList } = usePermissions();
    const listPermissionsSvc = useList();
    const permissions = listPermissionsSvc.data || [];
    const groupedPermissions = groupPermissions(permissions);

    const { watch, setValue } = useFormContext();
    const selectedPermissions = watch("Permissions") || [];

    const handleTogglePermission = (permId: string) => {
        const isSelected = selectedPermissions.some((p: any) => p.Id === permId);
        if (isSelected) {
            setValue(
                "Permissions",
                selectedPermissions.filter((p: any) => p.Id !== permId),
                { shouldDirty: true },
            );
        } else {
            const perm = permissions.find((p: any) => p.Id === permId);
            setValue("Permissions", [...selectedPermissions, perm], { shouldDirty: true });
        }
    };

    const handleSelectAll = (isSelect: boolean) => {
        if (isSelect) {
            setValue("Permissions", permissions, { shouldDirty: true });
        } else {
            setValue("Permissions", [], { shouldDirty: true });
        }
    };

    const handleToggleGroup = (groupPerms: any[], isSelect: boolean) => {
        const otherPermissions = selectedPermissions.filter((sp: any) => !groupPerms.some((gp) => gp.Id === sp.Id));

        if (isSelect) {
            setValue("Permissions", [...otherPermissions, ...groupPerms], { shouldDirty: true });
        } else {
            setValue("Permissions", otherPermissions, { shouldDirty: true });
        }
    };

    const isAllSelected = selectedPermissions.length === permissions.length && permissions.length > 0;

    return (
        <Box flex={1}>
            <Box mt={0} display={"flex"} flexDirection={"column"} gap={3}>
                <TextField
                    name={"Name"}
                    label={"Role Name"}
                    helperText={"Descriptive name (e.g. Project Manager)"}
                    placeholder={"e.g., Project Manager"}
                />
                <Textarea
                    name={"Description"}
                    label={"Description"}
                    helperText={"Briefly describe role responsibilities"}
                    placeholder={"e.g., Handles project scheduling and team coordinates"}
                    minRows={3}
                />

                <Box mt={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={2}>
                        <Box>
                            <Typography level="title-md">Permissions Matrix</Typography>
                            <Typography level="body-sm">Configure granular access levels for this role.</Typography>
                        </Box>
                        <Button
                            variant="plain"
                            size="sm"
                            onClick={() => handleSelectAll(!isAllSelected)}
                            startDecorator={isAllSelected ? <Circle size={16} /> : <CheckCircle2 size={16} />}
                        >
                            {isAllSelected ? "Unselect All" : "Select All"}
                        </Button>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {Object.entries(groupedPermissions).map(([group, perms]) => {
                        const groupSelectedCount = perms.filter((p) =>
                            selectedPermissions.some((sp: any) => sp.Id === p.Id),
                        ).length;
                        const isGroupFull = groupSelectedCount === perms.length;
                        const isGroupEmpty = groupSelectedCount === 0;

                        return (
                            <Box key={group} mb={4}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography
                                            level="title-sm"
                                            sx={{
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em",
                                                fontWeight: 800,
                                                color: isGroupEmpty ? "text.secondary" : "primary.plainColor",
                                            }}
                                        >
                                            {group}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            ({groupSelectedCount} / {perms.length} selected)
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="soft"
                                        color="neutral"
                                        size="sm"
                                        sx={{ minHeight: "unset", py: 0.5, fontSize: "xs" }}
                                        onClick={() => handleToggleGroup(perms, !isGroupFull)}
                                        startDecorator={isGroupFull ? <ListX size={14} /> : <ListPlus size={14} />}
                                    >
                                        {isGroupFull ? "Unselect All" : "Select All"}
                                    </Button>
                                </Box>

                                <Grid container spacing={1.5}>
                                    {perms.map((perm) => {
                                        const isSelected = selectedPermissions.some((p: any) => p.Id === perm.Id);
                                        return (
                                            <Grid key={perm.Id} xs={12} sm={6} md={4} lg={3}>
                                                <Box
                                                    onClick={() => handleTogglePermission(perm.Id)}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.5,
                                                        p: 1.2,
                                                        px: 1.5,
                                                        borderRadius: "lg",
                                                        border: "1px solid",
                                                        borderColor: isSelected
                                                            ? "primary.solidBg"
                                                            : "neutral.outlinedBorder",
                                                        bgcolor: isSelected ? "primary.softBg" : "background.surface",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                        boxShadow: isSelected ? "sm" : "none",
                                                        "&:hover": {
                                                            borderColor: isSelected
                                                                ? "primary.solidBg"
                                                                : "primary.outlinedBorder",
                                                            bgcolor: isSelected
                                                                ? "primary.softHoverBg"
                                                                : "primary.softBg",
                                                            transform: "translateY(-1px)",
                                                            boxShadow: "md",
                                                        },
                                                        "&:active": {
                                                            transform: "translateY(0)",
                                                        },
                                                    }}
                                                >
                                                    <JoyCheckbox
                                                        size="sm"
                                                        checked={isSelected}
                                                        color={isSelected ? "primary" : "neutral"}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleTogglePermission(perm.Id);
                                                        }}
                                                    />
                                                    <Box overflow="hidden">
                                                        <Typography
                                                            level="body-xs"
                                                            sx={{
                                                                fontWeight: isSelected ? 700 : 500,
                                                                color: isSelected
                                                                    ? "primary.plainColor"
                                                                    : "text.primary",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {(perm.Name || perm.Id).split(":").slice(-1)[0].trim()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
